/* Reconstructing a past book value from real closing prices.
 *
 * What this produces is honest arithmetic on real numbers — Yahoo's daily closes
 * multiplied by the shares held today — but it is deliberately NOT called
 * history, because it answers a different question than the recorded snapshots
 * do:
 *
 *   a snapshot says   what the account was worth that day
 *   a rebuilt point says   what today's holdings would have been worth that day
 *
 * They diverge by exactly the trading done in between, and that divergence is
 * worth seeing rather than smoothing away. So rebuilt points are tagged, drawn
 * differently, and never overwrite a measurement. */

import type { DailyHistory } from '../api/client';
import type { Holding, Snapshot } from '../types';

export interface RebuildInput {
  history: DailyHistory[];
  holdings: Holding[];
  /** Everything that is not shares: cash, margin posted, and the unrealised on
      open contracts. Held flat, because the dates those positions opened and
      closed are not in the record. */
  nonEquityUsd: number;
  /** Rates for converting a non-USD listing. */
  usdPerUnit: (currency: string) => number | null;
  /** Snapshots already recorded, which win wherever they overlap. */
  existing: Snapshot[];
  /** Today's USD value of holdings whose symbol returned no history at all.
      Held flat for the same reason cash is: leaving them out entirely would sink
      every past point by their whole value and draw a rise that never happened. */
  unpricedUsd?: number;
  /** ISO date. Nothing before it, so the line never runs back past the day the
      book began — valuing today's shares in a year when the account did not hold
      them answers a question nobody asked. */
  startFrom?: string;
}

export interface RebuildResult {
  snapshots: Snapshot[];
  /** Tickers with no price history, so their value is held flat at today's. */
  missing: string[];
  from: string | null;
  to: string | null;
  /** Days where at least one name did not trade and kept its previous close. */
  carriedDays: number;
}

/* Which version of this file produced the points now stored.
 *
 * The automatic backfill runs once and then stands back, so that a line the
 * user has edited is not quietly rewritten under them. That is right until the
 * rebuild itself gets better: a book filled by an older version keeps its old,
 * narrower line for ever, and the only way out is a settings screen the user
 * has to know to visit. Stamping what produced the points lets one improved
 * version refill itself, once, on every device — and no more than once.
 *
 * Bump this whenever a change here would produce a materially different line.
 *   1  three-month window, days every holding traded, names without history dropped
 *   2  full window back to the first lot, days any holding traded, last close
 *      carried across a name's quiet day, names without history held flat
 */
export const REBUILD_GENERATION = 2;

/** The narrowest window Yahoo accepts that still reaches back to `from`. Asking
    for more is slower and returns years the book never lived through. */
export function yahooRangeCovering(from: string | undefined, now = new Date()): string {
  if (!from) return '3mo';
  const days = (now.getTime() - new Date(`${from}T00:00:00Z`).getTime()) / 86_400_000;
  if (!Number.isFinite(days) || days <= 0) return '3mo';
  const steps: Array<[number, string]> = [
    [85, '3mo'], [175, '6mo'], [355, '1y'], [720, '2y'], [1800, '5y'],
  ];
  for (const [limit, range] of steps) if (days <= limit) return range;
  return 'max';
}

export function rebuildHistory(input: RebuildInput): RebuildResult {
  const {
    history, holdings, nonEquityUsd, usdPerUnit, existing,
    unpricedUsd = 0, startFrom,
  } = input;

  const sharesBySymbol = new Map<string, number>();
  for (const h of holdings) {
    sharesBySymbol.set(h.yahooSymbol, (sharesBySymbol.get(h.yahooSymbol) ?? 0) + h.shares);
  }

  const priced = new Map<string, DailyHistory>();
  for (const h of history) if (h.days.length > 0) priced.set(h.symbol, h);

  const missing = [...sharesBySymbol.keys()].filter((s) => !priced.has(s));

  /* Every day on which any holding traded, not only the days they all did.
     Taking the intersection let one short series — a recent listing, or a name
     the upstream answers for patchily — cut the whole book back to its length,
     which is what left the chart full of holes. */
  const everyDay = new Set<string>();
  for (const h of priced.values()) for (const d of h.days) everyDay.add(d.date);
  let days = [...everyDay].sort();
  if (startFrom) days = days.filter((d) => d >= startFrom);

  const empty: RebuildResult = { snapshots: [], missing, from: null, to: null, carriedDays: 0 };
  if (days.length === 0) return empty;

  const closes = new Map<string, Map<string, number>>();
  for (const [symbol, h] of priced) {
    closes.set(symbol, new Map(h.days.map((d) => [d.date, d.close])));
  }

  // A date already measured keeps its measurement; the rebuild fills around it.
  const measuredDates = new Set(
    existing.filter((s) => !s.reconstructed).map((s) => s.at.slice(0, 10)),
  );

  /* The last close seen for each name, carried forward across days it did not
     trade. A holiday on one exchange, or a halt, is not a change in what the
     position was worth — the last price struck is still the last price struck,
     and it is an observed number rather than an invented one. What cannot be
     carried is a price from before the name's history begins: there is nothing
     behind it, so those leading days are left out instead of counted short. */
  const lastClose = new Map<string, number>();
  const snapshots: Snapshot[] = [];
  let carriedDays = 0;

  for (const date of days) {
    let equityUsd = 0;
    let carried = false;
    let complete = true;

    for (const [symbol, shares] of sharesBySymbol) {
      const own = closes.get(symbol);
      if (!own) continue;                       // no history at all — see unpricedUsd
      const close = own.get(date);
      if (close !== undefined) lastClose.set(symbol, close);
      else if (lastClose.has(symbol)) carried = true;
      else { complete = false; break; }         // before this name's record starts

      const rate = usdPerUnit(priced.get(symbol)?.currency ?? 'USD');
      if (rate === null) { complete = false; break; }
      equityUsd += shares * lastClose.get(symbol)! * rate;
    }

    // The check comes after the loop above so a measured day still advances the
    // carried closes — skipping it outright would strand the days that follow.
    if (!complete || measuredDates.has(date)) continue;
    if (equityUsd <= 0) continue;

    if (carried) carriedDays++;
    snapshots.push({
      // Marked at the close, which is when the price it uses was struck.
      at: `${date}T21:00:00.000Z`,
      totalUsd: equityUsd + unpricedUsd + nonEquityUsd,
      contributedUsd: 0,
      reconstructed: true,
    });
  }

  if (snapshots.length === 0) return empty;

  return {
    snapshots,
    missing,
    from: snapshots[0]!.at.slice(0, 10),
    to: snapshots[snapshots.length - 1]!.at.slice(0, 10),
    carriedDays,
  };
}
